/**
 * Continue an MCP OAuth code flow through the app login page.
 *
 * The MCP server sends the browser here with the client's PKCE and redirect.
 * After the email lookup, that same PKCE goes to Auth0 or Cognito. The code
 * comes back with an `a.` or `c.` prefix so mcp-tools can pick the token URL.
 *
 * The IdP `state` is only a nonce stored in this browser. A link that carries
 * someone else's state is ignored. Sign-in stays in the window the client
 * opened, because ChatGPT only reads the code in that window.
 */

export type McpIdpConfig = {
  auth0Domain: string;
  auth0ClientId: string;
  audience: string;
  cognitoDomain: string;
  cognitoClientId: string;
  origin: string;
};

const _STORAGE_KEY = 'eptai_mcp_oauth_handoff';
const _COOKIE_KEY = 'eptai_mcp_oauth_handoff';

// App reads this twice on one load (Auth0 skip flag, then the redirect).
// The first read removes the stored nonce, so the second must see the same result.
let _cachedReturn: { search: string; url: string | null } | null = null;

/** Full redirect URIs already registered for MCP clients. */
const _EXACT_REDIRECTS = new Set([
  'https://claude.ai/api/mcp/auth_callback',
  'https://claude.com/api/mcp/auth_callback',
  'https://preview.claude.ai/api/mcp/auth_callback',
  'https://platform.openai.com/apps-manage/oauth',
  'https://chatgpt.com/connector_platform_oauth_redirect',
  'https://cursor.com/agents/mcp/oauth/callback',
  'https://www.cursor.com/agents/mcp/oauth/callback',
  'http://localhost:8787/callback',
  'http://127.0.0.1:8787/callback',
  'http://localhost:6274/oauth/callback',
  'http://127.0.0.1:6274/oauth/callback',
  'http://localhost:6274/oauth/callback/debug',
  'http://127.0.0.1:6274/oauth/callback/debug',
]);

type _StoredHandoff = {
  nonce: string;
  provider: 'auth0' | 'cognito';
  redirectUri: string;
  clientState: string;
  issuer: string | null;
};

function _allowedRedirect(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.username || url.password || url.search || url.hash) {
    return false;
  }
  const normalized = `${url.origin}${url.pathname}`;
  if (_EXACT_REDIRECTS.has(normalized)) {
    return true;
  }
  // Each ChatGPT connector gets its own id. The host and path shape stay fixed.
  return (
    url.protocol === 'https:' &&
    url.hostname === 'chatgpt.com' &&
    /^\/connector\/oauth\/[A-Za-z0-9_-]+$/.test(url.pathname)
  );
}

function _httpsIssuer(value: string | null): string | null {
  if (!value) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) {
    return null;
  }
  return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
}

function _newNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

function _parseHandoff(raw: string | null): _StoredHandoff | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<_StoredHandoff>;
    if (
      typeof parsed.nonce !== 'string' ||
      (parsed.provider !== 'auth0' && parsed.provider !== 'cognito') ||
      typeof parsed.redirectUri !== 'string' ||
      typeof parsed.clientState !== 'string' ||
      !_allowedRedirect(parsed.redirectUri)
    ) {
      return null;
    }
    return {
      nonce: parsed.nonce,
      provider: parsed.provider,
      redirectUri: parsed.redirectUri,
      clientState: parsed.clientState,
      issuer: typeof parsed.issuer === 'string' ? _httpsIssuer(parsed.issuer) : null,
    };
  } catch {
    return null;
  }
}

function _writeHandoffCookie(value: string, maxAge: number): void {
  // SameSite=None so the Auth0 redirect back into ChatGPT's popup still carries
  // this. Lax is dropped on that cross-site hop, and then the code is lost.
  const secure = window.location.protocol === 'https:';
  const sameSite = secure ? 'None' : 'Lax';
  const secureAttr = secure ? '; Secure' : '';
  document.cookie = `${_COOKIE_KEY}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=${sameSite}${secureAttr}`;
}

function _readHandoffCookie(): string | null {
  const prefix = `${_COOKIE_KEY}=`;
  const part = document.cookie.split('; ').find(item => item.startsWith(prefix));
  if (!part) {
    return null;
  }
  try {
    return decodeURIComponent(part.slice(prefix.length));
  } catch {
    return null;
  }
}

function _saveHandoff(handoff: _StoredHandoff): void {
  const raw = JSON.stringify(handoff);
  sessionStorage.setItem(_STORAGE_KEY, raw);
  // The Auth0 return window cannot see this tab's sessionStorage. The cookie
  // and localStorage can, as long as Auth0 echoes our nonce.
  localStorage.setItem(_STORAGE_KEY, raw);
  _writeHandoffCookie(raw, 600);
}

function _clearHandoff(nonce: string): void {
  if (_parseHandoff(sessionStorage.getItem(_STORAGE_KEY))?.nonce === nonce) {
    sessionStorage.removeItem(_STORAGE_KEY);
  }
  if (_parseHandoff(localStorage.getItem(_STORAGE_KEY))?.nonce === nonce) {
    localStorage.removeItem(_STORAGE_KEY);
  }
  if (_parseHandoff(_readHandoffCookie())?.nonce === nonce) {
    _writeHandoffCookie('', 0);
  }
}

/**
 * Return the handoff this browser started, and forget it.
 *
 * Example input: nonce stored by `buildMcpIdpUrl`
 * Example output: that handoff, or null when the nonce was not stored here.
 */
function _handoffForNonce(nonce: string): _StoredHandoff | null {
  return (
    [
      _parseHandoff(sessionStorage.getItem(_STORAGE_KEY)),
      _parseHandoff(localStorage.getItem(_STORAGE_KEY)),
      _parseHandoff(_readHandoffCookie()),
    ].find(item => item?.nonce === nonce) ?? null
  );
}

function _takeHandoff(nonce: string): _StoredHandoff | null {
  const handoff = _handoffForNonce(nonce);
  if (!handoff) {
    return null;
  }
  _clearHandoff(nonce);
  return handoff;
}

/**
 * Read an MCP authorize request off the login page query string.
 *
 * Example input: `?response_type=code&redirect_uri=http://localhost:8787/callback&code_challenge=abc`
 * Example output: `{ redirectUri }` or null for a normal login.
 */
export function readMcpAuthorizeRequest(search: string): { redirectUri: string } | null {
  const params = new URLSearchParams(search);
  const redirectUri = params.get('redirect_uri') ?? '';
  const method = params.get('code_challenge_method');
  if (
    params.get('response_type') !== 'code' ||
    !params.get('code_challenge') ||
    (method !== null && method !== 'S256') ||
    !_allowedRedirect(redirectUri)
  ) {
    return null;
  }
  return { redirectUri };
}

/**
 * Build the Auth0 or Cognito authorize URL without replacing the client's PKCE.
 *
 * Example input: provider `auth0`, email `ada@ept.ai`, search with `code_challenge=chal`
 * Example output: `https://login.ept.ai/authorize?...&code_challenge=chal&login_hint=ada%40ept.ai`
 */
export function buildMcpIdpUrl(
  provider: 'auth0' | 'cognito',
  email: string,
  search: string,
  config: McpIdpConfig
): string | null {
  const params = new URLSearchParams(search);
  const redirectUri = params.get('redirect_uri') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  if (!readMcpAuthorizeRequest(search)) {
    return null;
  }
  const nonce = _newNonce();
  _saveHandoff({
    nonce,
    provider,
    redirectUri,
    clientState: params.get('state') ?? '',
    issuer: _httpsIssuer(params.get('iss')),
  });
  if (provider === 'cognito') {
    const domain = email.split('@')[1] ?? '';
    if (!config.cognitoDomain || !config.cognitoClientId || !domain) {
      _clearHandoff(nonce);
      return null;
    }
    const cognitoParams = new URLSearchParams({
      client_id: config.cognitoClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: `${config.origin}/login`,
      state: nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      idp_identifier: domain,
      login_hint: email,
    });
    return `https://${config.cognitoDomain}/oauth2/authorize?${cognitoParams.toString()}`;
  }
  const auth0Params = new URLSearchParams({
    client_id: config.auth0ClientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: config.origin,
    audience: config.audience,
    state: nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    login_hint: email,
  });
  const domain = config.auth0Domain.replace(/^https?:\/\//, '');
  return `https://${domain}/authorize?${auth0Params.toString()}`;
}

/** Leave the login page for the IdP authorize URL from `buildMcpIdpUrl`. */
export function goToIdp(url: string): void {
  window.location.assign(url);
}

/**
 * Send this window to the MCP client's callback.
 *
 * ChatGPT only accepts the code in the window it opened, so sign-in stays
 * in that window and the code leaves from here.
 *
 * Example input: `https://chatgpt.com/connector_platform_oauth_redirect?code=a.abc&state=s`
 * Example output: this window loads that URL.
 */
export function deliverMcpReturn(url: string): void {
  window.location.replace(url);
}

/**
 * Where to send the browser after Auth0 or Cognito returns.
 *
 * Example input: `?code=abc&state=<nonce stored in this browser>`
 * Example output: `http://localhost:8787/callback?code=a.abc&state=client-state`
 */
export function mcpClientReturnUrl(search: string): string | null {
  if (_cachedReturn?.search === search) {
    return _cachedReturn.url;
  }
  const url = _clientReturnUrl(search);
  _cachedReturn = { search, url };
  return url;
}

function _clientReturnUrl(search: string): string | null {
  const params = new URLSearchParams(search);
  const state = params.get('state');
  const code = params.get('code');
  const error = params.get('error');
  if (!state || (!code && !error)) {
    return null;
  }
  const handoff = _takeHandoff(state);
  if (!handoff) {
    return null;
  }
  const target = new URL(handoff.redirectUri);
  target.searchParams.set('state', handoff.clientState);
  if (handoff.issuer) {
    target.searchParams.set('iss', handoff.issuer);
  }
  if (code) {
    const prefix = handoff.provider === 'cognito' ? 'c.' : 'a.';
    target.searchParams.set('code', `${prefix}${code}`);
  } else if (error) {
    target.searchParams.set('error', error);
    const description = params.get('error_description');
    if (description) {
      target.searchParams.set('error_description', description);
    }
  }
  return target.toString();
}
